/**
 * Assistente de IA (menu do nome → Assistente de IA): tira dúvidas de uso com base no Manual do PMIS, com o link da
 * seção de onde veio a resposta. Não vê dados de projetos. A conversa fica só nesta aba do navegador (o servidor guarda
 * só a contagem, para os limites).
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, errorMessage } from '../lib/api';
import { parseMarkdown, renderBlocks, type LinkTarget } from '../lib/markdown';
import { Alert, Button, Dialog, Textarea } from './ui';
import { Icon } from './pmis';

interface Msg { role: 'user' | 'assistant'; text: string; sources?: { title: string; href: string }[]; suggestions?: string[]; error?: boolean }
interface Status { canUse: boolean; active: boolean; usedToday: number; perUserPerDay: number }

const KEY = 'pmis.assistant';
const STARTERS = ['Como mudo a fase de um projeto?', 'Como lanço minhas horas da semana?', 'O que quer dizer "Falta TE" no painel?', 'Como funciona a reunião semanal?'];

const load = (): Msg[] => { try { return JSON.parse(sessionStorage.getItem(KEY) ?? '[]') as Msg[]; } catch { return []; } };
const store = (m: Msg[]) => { try { sessionStorage.setItem(KEY, JSON.stringify(m.slice(-30))); } catch { /* sem armazenamento: a conversa só não sobrevive à recarga */ } };

/** Endereço de um link da resposta: capítulo do manual (arquivo.md#ancora), externo ou texto. */
const link = (href: string): LinkTarget => {
  if (/^https?:\/\//.test(href)) return { kind: 'external', href };
  const m = /^(?:\.\/)?([\w-]+)\.md(?:#(.+))?$/.exec(href);
  return m ? { kind: 'chapter', chapter: m[1]!, anchor: m[2] } : { kind: 'text' };
};

export function AssistantDialog({ onClose, screen }: { onClose: () => void; screen?: string }) {
  const nav = useNavigate();
  const [msgs, setMsgs] = useState<Msg[]>(load);
  const [q, setQ] = useState('');
  const end = useRef<HTMLDivElement>(null);
  const status = useQuery({ queryKey: ['assistant-status'], queryFn: () => api<Status>('/api/v1/assistant/status') });
  useEffect(() => { store(msgs); end.current?.scrollIntoView({ block: 'end' }); }, [msgs]);

  const ask = useMutation({
    mutationFn: (question: string) => api<{ answer: string; suggestions: string[]; sources: { title: string; href: string }[] }>('/api/v1/assistant/ask', {
      method: 'POST', body: { question, screen, history: msgs.filter((m) => !m.error).map((m) => ({ role: m.role, text: m.text.slice(0, 6000) })) },
    }),
    onSuccess: (r) => { setMsgs((m) => [...m, { role: 'assistant', text: r.answer, sources: r.sources, suggestions: r.suggestions }]); void status.refetch(); },
    onError: (e) => setMsgs((m) => [...m, { role: 'assistant', text: errorMessage(e), error: true }]),
  });
  const send = (text: string) => {
    const t = text.trim();
    if (t.length < 2 || ask.isPending) return;
    setMsgs((m) => [...m, { role: 'user', text: t }]);
    setQ('');
    ask.mutate(t);
  };
  const open = (t: LinkTarget) => {
    if (t.kind === 'external') { window.open(t.href, '_blank', 'noopener'); return; }
    if (t.kind !== 'chapter') return;
    onClose();
    nav(`/manual/${t.chapter}`);
    setTimeout(() => (t.anchor ? document.getElementById(t.anchor)?.scrollIntoView() : window.scrollTo(0, 0)), 80);
  };

  const s = status.data;
  const blocked = status.isError || (s && (!s.canUse || !s.active));
  const last = msgs[msgs.length - 1];
  return (
    <Dialog open wide onClose={onClose} title="Assistente de IA"
      footer={<>
        {msgs.length ? <Button variant="ghost" className="mr-auto" onClick={() => setMsgs([])}><Icon name="refresh" className="size-4" />Nova conversa</Button> : null}
        <Button onClick={onClose}>Fechar</Button>
      </>}>
      <p className="-mt-1 mb-3 text-xs text-muted">
        Responde dúvidas de uso com base no Manual do PMIS, com o link da seção. Não vê projetos, clientes nem horas; e-mails e telefones são tirados antes do envio.
        {s?.active && s.canUse ? ` Hoje: ${s.usedToday} de ${s.perUserPerDay} usos da IA.` : ''}
      </p>
      {status.isError ? <Alert>Não deu para falar com o assistente agora ({errorMessage(status.error)}). Tente de novo em instantes.</Alert> : null}
      {s && !s.canUse ? <Alert>Seu perfil não usa o Assistente de IA. Quem dá a permissão (Usar a IA) é o Administrador, em Administração › Perfis e permissões.</Alert> : null}
      {s && s.canUse && !s.active ? (
        <Alert>O Assistente de IA ainda não foi ligado. O Administrador liga em Administração › Integrações › Assistente de IA
          {' '}(<button type="button" className="underline" onClick={() => open({ kind: 'chapter', chapter: '29-integracao-assistente-ia' })}>guia no Manual</button>).
          Enquanto isso, o <button type="button" className="underline" onClick={() => open({ kind: 'chapter', chapter: 'README' })}>Manual</button> tem tudo, com busca.</Alert>
      ) : null}

      <div className="grid max-h-[55vh] min-h-48 gap-3 overflow-y-auto pr-1" aria-live="polite">
        {!msgs.length && !blocked ? (
          <div className="grid gap-2">
            <p className="text-sm text-muted">Pergunte como fazer algo no PMIS. Por exemplo:</p>
            <div className="flex flex-wrap gap-2">
              {STARTERS.map((t) => <button key={t} type="button" className="rounded-full border border-line px-3 py-1 text-sm hover:border-accent hover:text-accent" onClick={() => send(t)}>{t}</button>)}
            </div>
          </div>
        ) : null}
        {msgs.map((m, i) => m.role === 'user' ? (
          <div key={i} className="ml-auto max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-accent/15 px-3 py-2 text-sm">{m.text}</div>
        ) : (
          <div key={i} className={`max-w-[92%] rounded-2xl rounded-bl-sm border px-3 py-2 text-sm ${m.error ? 'border-err/40 text-err' : 'border-line bg-panel-2/50'}`}>
            {m.error ? m.text : <div className="grid gap-2">{renderBlocks(parseMarkdown(m.text), { link, onNavigate: open }, `a${i}`, true)}</div>}
            {m.sources?.length ? (
              <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-line pt-2 text-xs text-muted">
                Do manual:{m.sources.map((src) => <button key={src.href} type="button" className="rounded-full border border-line px-2 py-0.5 hover:border-accent hover:text-accent" onClick={() => open(link(src.href))}>{src.title}</button>)}
              </div>
            ) : null}
          </div>
        ))}
        {ask.isPending ? <div className="max-w-[92%] rounded-2xl rounded-bl-sm border border-line px-3 py-2 text-sm text-muted">Procurando no manual…</div> : null}
        {last?.role === 'assistant' && last.suggestions?.length && !ask.isPending && !blocked ? (
          <div className="flex flex-wrap gap-2">
            {last.suggestions.map((t) => <button key={t} type="button" className="rounded-full border border-line px-3 py-1 text-xs hover:border-accent hover:text-accent" onClick={() => send(t)}>{t}</button>)}
          </div>
        ) : null}
        <div ref={end} />
      </div>

      <form className="mt-3 flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); send(q); }}>
        <Textarea rows={2} className="flex-1" value={q} onChange={(e) => setQ(e.target.value)} maxLength={2000} disabled={!!blocked} data-autofocus
          placeholder={blocked ? 'Assistente indisponível' : 'Escreva a pergunta (Enter envia; Shift+Enter quebra a linha)'} aria-label="Pergunta"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(q); } }} />
        <Button type="submit" variant="primary" disabled={!!blocked || ask.isPending || q.trim().length < 2}><Icon name="sparkles" className="size-4" />Perguntar</Button>
      </form>
    </Dialog>
  );
}
